import { useCallback, useEffect, useRef, useState } from "react";
import {
  clampFacilitiesDrawerWidth,
  readFacilitiesDrawerWidth,
  writeFacilitiesDrawerWidth,
} from "@utils/facilitiesDrawerWidth";

export function useFacilitiesDrawerWidth() {
  const [width, setWidth] = useState(() => readFacilitiesDrawerWidth());
  const widthRef = useRef(width);
  widthRef.current = width;

  const isResizingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const beginResize = useCallback((clientX: number) => {
    isResizingRef.current = true;
    startXRef.current = clientX;
    startWidthRef.current = widthRef.current;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  const handleResizeMouseDown = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      beginResize(event.clientX);
    },
    [beginResize],
  );

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isResizingRef.current) return;
      const delta = event.clientX - startXRef.current;
      setWidth(clampFacilitiesDrawerWidth(startWidthRef.current + delta));
    };

    const finishResize = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      writeFacilitiesDrawerWidth(widthRef.current);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", finishResize);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", finishResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  return {
    width,
    handleResizeMouseDown,
  };
}
